import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  OnDestroy,
  PLATFORM_ID
} from '@angular/core';
import {Cart, WineProduct} from '@core/interfaces/generated/graphql';
import {CartService} from '@core/services/cart.service';
import {Observable} from 'rxjs';
import {PriceTableComponent} from '@layout/components/product/price-table/price-table.component';
import {ProductRatingsComponent} from '../product-ratings/product-ratings.component';
import {AsyncPipe, isPlatformBrowser, NgOptimizedImage} from '@angular/common';
import {StoryblokService} from '@core/services/storyblok.service';
import {map} from 'rxjs/operators';
import {formatPrice} from '@core/pipes/price.pipe';
import {AlgoliaInsightsService} from '@core/services/algolia-insights.service';
import {RouterModule} from '@angular/router';
import {GroupVoucherService} from '@core/services/groupVoucher.service';
import {GridService} from '@core/services/grid.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ProductTopCtaComponent} from '@layout/components/product/product-top-cta/product-top-cta.component';
import {
  ProductStickyBuyBarComponent
} from '@modules/page/components/product/components/product-sticky-buy-bar/product-sticky-buy-bar.component';
import {StoreConfigService} from '@core/services/store-config.service';
import {
  ProductBadgesComponent
} from '@modules/page/components/product/components/product-badges/product-badges.component';
import {
  ProductDeliveryCountdownComponent
} from '@modules/page/components/product/components/product-delivery-countdown/product-delivery-countdown.component';
import {
  TrustpilotMiniWidgetComponent
} from '@layout/components/atoms/trustpilot-mini-widget/trustpilot-mini-widget.component';

@Component({
  selector: 'app-product-right',
  templateUrl: './product-right.component.html',
  styleUrls: ['./product-right.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgOptimizedImage, PriceTableComponent, ProductRatingsComponent, ProductDeliveryCountdownComponent, AsyncPipe, RouterModule, ProductTopCtaComponent, ProductStickyBuyBarComponent, ProductBadgesComponent, TrustpilotMiniWidgetComponent]
})
export class ProductRightComponent implements AfterViewInit, OnDestroy{
  storyblokService = inject(StoryblokService);
  cartService = inject(CartService);
  private algoliaInsightsService = inject(AlgoliaInsightsService);
  private groupVoucherService = inject(GroupVoucherService);
  private gridService = inject(GridService);
  private modal = inject(NgbModal);
  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);
  private storeConfigService = inject(StoreConfigService);


  private observer!: IntersectionObserver;

  readonly product = input<WineProduct>();

  currencyCode = this.storeConfigService.currencyCode;
  showStickyBar = false;
  cart: Cart;

  totalPriceSignal = computed(() => {
    return this.getTotalPrice(this.cartService.cartSignal().cart);
  });

  ngAfterViewInit() {
    const priceTable = this.el.nativeElement.querySelector('app-price-table');

    if (priceTable && isPlatformBrowser(this.platformId)) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.showStickyBar = !entry.isIntersecting;
          this.cdr.detectChanges();
        },
        {root: null, threshold: 0.3}
      );
      this.observer.observe(priceTable);
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  getTotalPrice(cart: Cart): number {
    const freeDelivery = this.storeConfigService.storeConfig()?.freeDeliveryStartsFrom || 0;
    const totalPrice = freeDelivery - formatPrice(cart?.totalPrice);
    if (totalPrice) {
      return totalPrice;
    }
    return freeDelivery;
  }

  hasGroupVoucherPrice(): Observable<boolean> {
    return this.groupVoucherService.getGroupVoucher().pipe(
      map(groupVoucher => {
        return !!groupVoucher?.prices?.find(d => d?.vismaId == this.product()?.vismaProductNumber);
        }
      ));
  }

  get isMobile() {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    return !this.gridService.isLarge;
  }

  showFreeShipping(): Observable<boolean> {
    return this.groupVoucherService.getGroupVoucher().pipe(
      map(groupVoucher => {
          return !groupVoucher?.delivery;
        }
      ));
  }

  addedToCart() {
    const product = this.product();
    if (!product?.id) {
      return;
    }
    const queryId = history.state.queryID;
    this.algoliaInsightsService.clickedAddToCart(product?.id, queryId);
  }

  openEnergyLabelDoc() {
    import('../../../../../../layout/components/modals/image-modal/image-modal.component').then(comp => {
      const ref = this.modal.open(comp.ImageModalComponent);
      ref.componentInstance.title = $localize`Energimærke`;
      ref.componentInstance.imgSrc = this.product()?.meta?.energyLabelDoc;
    })
  }

  get isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
